import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ConfLevel } from './types';

export const AiChip = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: 11.5,
      fontWeight: 600,
      color: 'primary.dark',
      bgcolor: 'primary.50',
      px: 1.1,
      py: 0.4,
      borderRadius: 9999,
    }}
  >
    <AutoAwesomeIcon sx={{ fontSize: 13 }} />
    IA
  </Box>
);

interface OptionHeadProps {
  n: string;
  icon: ReactNode;
  title: string;
}

export const OptionHead = ({ n, icon, title }: OptionHeadProps) => (
  <Stack direction="row" alignItems="center" spacing={1.4}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '11px',
        bgcolor: 'primary.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{ fontSize: 11.5, fontWeight: 700, color: 'primary.dark', letterSpacing: '.05em' }}
      >
        OPCIÓN {n}
      </Typography>
      <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
    </Box>
  </Stack>
);

interface FieldHeadProps {
  label: string;
  conf?: ConfLevel;
  edited?: boolean;
}

export const FieldHead = ({ label, conf, edited }: FieldHeadProps) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
    <Typography
      sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
    {conf && <ConfMark conf={conf} edited={edited} />}
  </Stack>
);

interface FieldProps {
  label: string;
  conf?: ConfLevel;
  edited?: boolean;
  children: ReactNode;
}

export const Field = ({ label, conf, edited, children }: FieldProps) => {
  const amber = conf === 'low' && !edited;
  return (
    <Box>
      <FieldHead label={label} conf={conf} edited={edited} />
      <Box
        sx={{
          borderRadius: '10px',
          border: 1.5,
          borderColor: amber
            ? (theme) => theme.palette.tokens.semantic.warningFieldBorder
            : 'grey.200',
          bgcolor: amber
            ? (theme) => theme.palette.tokens.semantic.warningFieldSurface
            : 'background.paper',
          transition: 'all .15s',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface ConfMarkProps {
  conf: ConfLevel;
  edited?: boolean;
}

const ConfMark = ({ conf, edited }: ConfMarkProps) => {
  if (edited) {
    return (
      <ConfPill
        sx={{ color: 'success.main', bgcolor: 'success.50' }}
        icon={<CheckIcon sx={{ fontSize: 11 }} />}
      >
        Editado
      </ConfPill>
    );
  }

  if (conf === 'low') {
    return (
      <ConfPill
        sx={{
          color: (theme) => theme.palette.tokens.semantic.warningText,
          bgcolor: (theme) => theme.palette.tokens.semantic.warningSurface,
        }}
        icon={<WarningAmberIcon sx={{ fontSize: 11 }} />}
      >
        Revisar
      </ConfPill>
    );
  }

  if (conf === 'med') {
    return (
      <ConfPill
        sx={{ color: 'text.secondary', bgcolor: 'grey.50' }}
        icon={<AutoAwesomeIcon sx={{ fontSize: 11 }} />}
      >
        Sugerido
      </ConfPill>
    );
  }

  return (
    <ConfPill
      sx={{ color: 'primary.dark', bgcolor: 'primary.50' }}
      icon={<AutoAwesomeIcon sx={{ fontSize: 11 }} />}
    >
      IA
    </ConfPill>
  );
};

interface ConfPillProps {
  children: ReactNode;
  icon: ReactNode;
  sx: SxProps<Theme>;
}

const ConfPill = ({ children, icon, sx }: ConfPillProps) => (
  <Box
    component="span"
    sx={[
      {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 11,
        fontWeight: 600,
        px: 1,
        py: 0.25,
        borderRadius: 9999,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {icon}
    {children}
  </Box>
);
